// MesaController.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Button} from 'react-bootstrap';
import { Mesa, PedidoGroup } from '../../../../../types';
import { MdOutlineTableRestaurant } from "react-icons/md";
import { getActivePedidosByMesaId, getActivePedidosByPedidoGroupId } from '../../../../../../../../crud/pedidos_api/pedidos';
import { GetComandasByMesaId } from '../../../../../../../../crud/pedidos_api/comandas';
import { updateMesaCurrentPeople } from 'crud/management_api/mesas_info';
import { toast } from 'react-toastify';
import './MesaController.scss';
import { X } from 'react-bootstrap-icons';
import { getPermissionState } from '../../../../../../../../utils/functions';
import { usePedidos } from '../../../../../../Pedidos/PedidosContext';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import CobrarPedidos from '../../../../../../Pedidos/Common/CobrarPedidos/CobrarPedidos';
import RenderComandas from '../../../../../../Pedidos/Common/RenderComandas/RenderComandas';
import OrderTaker from '../../../../../../Pedidos/Common/OrderTaker/OrderTaker';
import OrderViewer from '../../../../../../Pedidos/Common/OrderViewer/OrderViewer';
import ChangeTable from './components/ChangeTable/ChangeTable';
import ResetTable from './components/ResetTable/ResetTable';
import ResetGroup from '../../../../../../Pedidos/Common/ResetGroup/ResetGroup';
import PedidoCommentModal from '../../../../../../Pedidos/Common/PedidoCommentModal/PedidoCommentModal';
import PedidoCommentsViewer from '../../../../../../Pedidos/Common/PedidoCommentsViewer/PedidoCommentsViewer';
import ChargedMesaWarning from '../../../../../../Pedidos/Restaurant/MasterMode/components/Sales/components/SalesFooter/ChargedMesaWarning';
import SplitReceiptButton from '../../../../../../Pedidos/Common/CobrarPedidos/components/SplitReceiptButton/SplitReceiptButton';
import { FaListCheck, FaReceipt, FaPlus, FaArrowsUpDown, FaTrashCan, FaMinus, FaUsers, FaComment} from 'react-icons/fa6';
import { FaUser } from 'react-icons/fa';

interface MesaControllerProps {
  mesa?: Mesa;
  pedidoGroup?: PedidoGroup;
  show: boolean;
  onClose: () => void;
  onMesaClosed?: (mesaId: number) => void; // NEW: Callback when mesa is closed after deleting last items
}

const MesaController: React.FC<MesaControllerProps> = ({ mesa, pedidoGroup, show, onClose, onMesaClosed }) => {
  // Determine if we're in group mode
  const isGroupMode = !!pedidoGroup && !mesa;
  
  // Refresca los pedidos activos de la mesa o grupo
  const refreshPedidos = () => {
    if (isGroupMode && pedidoGroup) {
      getActivePedidosByPedidoGroupId(pedidoGroup.pedido_group_id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
        })
        .catch((error) => {
          toast.error('Error actualizando los pedidos del grupo');
        });
      return;
    }
    
    if (!mesa) return;
    
    getActivePedidosByMesaId(mesa.id)
      .then((response) => {
        const data = response.data.reverse();
        setPedidos(data);
        const pedidosSelections = data.map((pedido: any) => pedido.selections);
        const allSelections = pedidosSelections.flat();
        setSelections(allSelections);
      })
      .catch((error) => {
        toast.error('Error actualizando los pedidos de la mesa');
      });
  };
  const {pedidos,setPedidos} = usePedidos();
  const [comandas, setComandas] = useState<any[]>([]);
  const [selections, setSelections] = useState<any>([]);
  const [peopleNumber, setPeopleNumber] = useState<number | null>(5);
  const [viewMode, setViewMode] = useState<'mesa' | 'comandas' | 'order' | 'payment'>('order');
  const [activeModal, setActiveModal] = useState<'reset' | 'move' | null>(null);
  const [loading, setLoading] = useState(true);
  const [orderContext, setOrderContext] = useState<any>(null);
  const [blocked, setBlocked] = useState(false);
  const [currentPeople, setCurrentPeople] = useState<number>(mesa?.current_people || 0);
  const [updatingPeople, setUpdatingPeople] = useState(false);
  const [targetSelections, setTargetSelections] = useState<string[] | undefined>(undefined); // NEW: For partial payments
  const [isSelectionMode, setIsSelectionMode] = useState(false); // NEW: Track if OrderViewer is in selection mode
  const [pedidoComment, setPedidoComment] = useState<string>('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCommentsViewer, setShowCommentsViewer] = useState(false);
  const [showChargedWarning, setShowChargedWarning] = useState(false);
  const [autoCreateSingle, setAutoCreateSingle] = useState(false); // NEW: Track if split button primary action was clicked
  const [selectedSplitMethod, setSelectedSplitMethod] = useState<'equal' | 'custom' | undefined>(undefined); // NEW: Track which dropdown option was selected

  // Keep currentPeople in sync with the mesa prop returned by the API.
  // This ensures the header counter shows the latest value coming from getSalonesByLocalId
  // even if the parent updates the mesa object while the modal is open.
  // Only applies to mesa mode, not group mode
  useEffect(() => {
    if (isGroupMode || !mesa) return;
    
    const apiValue = mesa.current_people ?? 1;
    setCurrentPeople(apiValue);
    // keep peopleNumber in sync too to ensure OrderTaker receives updated value
    setPeopleNumber(apiValue);
  }, [mesa?.current_people, isGroupMode, mesa]);

  const handleUpdatePeople = async (newCount: number) => {
    // Only works for mesa mode, not group mode
    if (isGroupMode || !mesa || newCount < 1 || updatingPeople) return;
    
    // Store previous value for rollback
    const previousCount = currentPeople;
    
    // Optimistic update - update UI immediately
    setCurrentPeople(newCount);
    try { mesa.current_people = newCount; } catch {}
    setPeopleNumber(newCount);
    
    setUpdatingPeople(true);
    try {
      // Make API call in background
      await updateMesaCurrentPeople(mesa.id, newCount);
      // Success - no toast needed, UI already updated
    } catch (error) {
      // Rollback on error
      setCurrentPeople(previousCount);
      try { mesa.current_people = previousCount; } catch {}
      setPeopleNumber(previousCount);
      toast.error('Error actualizando cantidad de personas');
      console.error('Error updating people count:', error);
    } finally {
      setUpdatingPeople(false);
    }
  };

  useEffect(() => {
    if (isGroupMode && pedidoGroup) {
      // Order context for pedido groups
      const orderContext = {
        mesa_id: null,
        mesa_num: pedidoGroup.name, // Use group name as mesa_num
        pedido_group_id: pedidoGroup.pedido_group_id,
        hora_entrega: null,
        client_id: null,
        client_name: null,
        priority: null,
        third_party_provider_code: null,
        third_party_provider_id: null,
        pedido_comment: pedidoComment || null,
      };
      setOrderContext(orderContext);
      return;
    }
    
    if (mesa) {
      // Order context for mesa
      const orderContext = {
        mesa_id: mesa.id,
        mesa_num: (mesa.tag === '' || mesa.tag === null) ? mesa.num : mesa.tag,
        pedido_group_id: null,
        hora_entrega: null,
        client_id: null,
        client_name: null,
        priority: null,
        third_party_provider_code: null,
        third_party_provider_id: null,
        pedido_comment: pedidoComment || null,
      };
      setOrderContext(orderContext);
    }
  }, [isGroupMode, pedidoGroup, mesa, pedidoComment]);

  useEffect(() => {
    // Initialize current people count when modal opens (mesa mode only)
    if (show && mesa && !isGroupMode) {
      const initial = mesa.current_people ?? 1;
      setCurrentPeople(initial);

      // also set peopleNumber so OrderTaker receives correct totalClients
      setPeopleNumber(initial);
    }
    
    // Reset pedido comment when modal opens (fresh session for each mesa/group)
    if (show) {
      setPedidoComment('');
      setAutoCreateSingle(false); // Reset auto-create flag
      setSelectedSplitMethod(undefined); // Reset selected split method
    }
  }, [show, mesa, isGroupMode]);

  useEffect(() => {
    // Handle group mode
    if (isGroupMode && pedidoGroup && show) {
      setViewMode('mesa');
      setLoading(true);
      
      // Fetch active pedidos for this group
      getActivePedidosByPedidoGroupId(pedidoGroup.pedido_group_id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          if (data.length === 0) {
            setSelections([]);
            setLoading(false);
            // If no pedidos, automatically show order taker
            setViewMode('order');
            return;
          }
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
          setLoading(false);
        })
        .catch((error) => {
          toast.error('Error obteniendo los pedidos del grupo');
          setLoading(false);
        });
      
      return;
    }
    
    // Handle mesa mode
    if (mesa && mesa.used && show) {
      // fetch active pedidos
      setViewMode('mesa');
      setLoading(true);
      getActivePedidosByMesaId(mesa.id)
        .then((response) => {
          const data = response.data.reverse();
          setPedidos(data);
          if (data.length === 0) {
            setSelections([]);
            setLoading(false);
            // If no pedidos, automatically show order taker
            setViewMode('order');
            return;
          }
          const pedidosSelections = data.map((pedido: any) => pedido.selections);
          const allSelections = pedidosSelections.flat();
          setSelections(allSelections);
          setLoading(false);
        })
        .catch((error) => {
          toast.error('Error obteniendo los pedidos de la mesa');
          setLoading(false);
        });

      // fetch comandas (only for mesa mode)
      GetComandasByMesaId(mesa.id)
        .then((response: any) => {
          setComandas(response.data.reverse());
        })
        .catch((error: any) => {
          toast.error('Error obteniendo las comandas de la mesa');
        });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesa?.id, isGroupMode, pedidoGroup?.pedido_group_id, show]); // Use mesa?.id to prevent re-running when only properties like 'charged' change

  useEffect(() => {
    // Attempt to auto-set peopleNumber from the first selection if existing
    if (selections.length !== 0) {
      const pnum = selections[0]?.totalPersons ?? 1;
      setPeopleNumber(pnum);
    }
  }, [selections]);

  useEffect(() => {
    // Reset when mesa is not used (only for mesa mode)
    if (!isGroupMode && mesa && !mesa.used) {
      setSelections([]);
      setPeopleNumber(null);
      setPedidoComment(''); // Reset comment when mesa is closed
    }
  }, [mesa, isGroupMode]);


  const refreshComandas = () => {
    if (!mesa) return;
    GetComandasByMesaId(mesa.id)
      .then((response: any) => {
        setComandas(response.data.reverse());
      })
      .catch((error: any) => {
        toast.error('Error obteniendo las comandas de la mesa');
      });
  };

  const handleToggleView = (mode: 'mesa' | 'comandas') => {
    console.log('handleToggleView called with mode:', mode, 'mesa:', mesa?.id);
    setViewMode(mode);
    // Re-fetch comandas when switching to comandas view
    if (mode === 'comandas' && mesa) {
      console.log('Fetching comandas for mesa:', mesa.id);
      GetComandasByMesaId(mesa.id)
        .then((response: any) => {
          console.log('Comandas response:', response.data);
          setComandas(response.data.reverse());
        })
        .catch((error: any) => {
          console.error('Error fetching comandas:', error);
          toast.error('Error obteniendo las comandas de la mesa');
        });
    }
  };

  const renderToggle = () => {
    return(
      <div className="mesa-options-toggle-buttons">
                <Button
                  variant="none"
                  onClick={() => handleToggleView('mesa')}
                  className={`view-mode-button ${viewMode === 'mesa' ? 'selected' : ''}`}
                >
                 <FaListCheck className='toggle-icon'/> Mesa
                </Button>
                <Button
                  variant="none"
                  onClick={() => handleToggleView('comandas')}
                  className={`view-mode-button ${viewMode === 'comandas' ? 'selected' : ''}`}
                >
                <FaReceipt className='toggle-icon'/> Comandas
                </Button>
              </div>
    )
  }

  const renderFooter = () => {
    // Determine CTA text based on mode and state
    const isUsed = isGroupMode ? pedidos.length > 0 : (mesa?.used || false);
    const ctaText = isUsed ? 'Agregar Pedido' : 'Tomar Pedido';
    
    // Get mesa identifier for display in warning modal
    const mesaIdentifier = mesa
      ? ((mesa.tag && mesa.tag.trim() !== '') ? mesa.tag : mesa.num)
      : pedidoGroup?.name;
    
    // Handle add order click - show warning if mesa is charged
    const handleAddOrderClick = () => {
      if (mesa?.charged) {
        setShowChargedWarning(true);
      } else {
        setViewMode('order');
      }
    };

    // Handle confirm from charged mesa warning
    const handleChargedWarningConfirm = () => {
      setShowChargedWarning(false);
      setViewMode('order');
    };
    
    return (
      <div className="mesa-controller-action-footer">
        {/* Primary CTA Button - Add Order */}
        <Button
          onClick={handleAddOrderClick}
          className="footer-cta-button footer-cta-primary"
          variant="none"
          disabled={blocked || isSelectionMode}
        >
          <FaPlus /> {ctaText}
        </Button>

        {/* Charged Mesa Warning Modal */}
        <ChargedMesaWarning
          show={showChargedWarning}
          onClose={() => setShowChargedWarning(false)}
          onConfirm={handleChargedWarningConfirm}
          mesaIdentifier={mesaIdentifier}
        />

        {/* Secondary CTA Button - Generate Bill (only if there are orders) */}
        {pedidos.length > 0 && getPermissionState(129) && (
          mesa?.charged ? (
            // Charged mesa - show simple "Ver Precuenta" button
            <Button
              className="footer-cta-button footer-cta-secondary"
              onClick={() => setViewMode('payment')}
              variant="none"
              disabled={blocked || isSelectionMode}
            >
              <FaReceipt /> Ver Precuenta
            </Button>
          ) : (
            // Not charged - show split button
            <SplitReceiptButton
              onGenerateSingle={() => {
                setAutoCreateSingle(true);
                setSelectedSplitMethod(undefined);
                setViewMode('payment');
              }}
              onShowSplitOptions={(method) => {
                setAutoCreateSingle(false);
                setSelectedSplitMethod(method);
                setViewMode('payment');
              }}
              disabled={blocked || isSelectionMode}
              variant="secondary"
            />
          )
        )}

        {/* Secondary Actions Row */}
        {pedidos.length > 0 && (
          <div className="footer-secondary-actions">
            {getPermissionState(122) && !selections.some((sel: any) => sel.payed === true) && (
              <>
                {mesa && (
                  <Button
                    className="footer-button footer-button-danger"
                    onClick={() => setActiveModal('reset')}
                    variant="outline-danger"
                    disabled={blocked || isSelectionMode}
                  >
                    <FaTrashCan /> Vaciar Mesa
                  </Button>
                )}
                {isGroupMode && pedidoGroup && (
                  <Button
                    className="footer-button footer-button-danger"
                    onClick={() => setActiveModal('reset')}
                    variant="outline-danger"
                    disabled={blocked || isSelectionMode}
                  >
                    <FaTrashCan /> Vaciar Grupo
                  </Button>
                )}
              </>
            )}

            {/* Move button only for mesas, not groups */}
            {mesa && !isGroupMode && (
              <Button
                className="footer-button footer-button-neutral"
                onClick={() => setActiveModal('move')}
                variant="none"
                disabled={blocked || isSelectionMode}
              >
                <FaArrowsUpDown /> Mover Mesa
              </Button>
            )}
          </div>
        )}

        {/* Modals */}
        {pedidos.length > 0 && (
          <>
            {mesa && !isGroupMode && (
              <>
                <ResetTable
                  show={activeModal === 'reset'}
                  onClose={() => setActiveModal(null)}
                  mesa={mesa}
                />

                <ChangeTable
                  show={activeModal === 'move'}
                  onClose={() => setActiveModal(null)}
                  mesa={mesa}
                />
              </>
            )}
            
            {isGroupMode && pedidoGroup && (
              <ResetGroup
                show={activeModal === 'reset'}
                onClose={() => setActiveModal(null)}
                pedidoGroup={pedidoGroup}
                onSuccess={() => {
                  // Refresh pedidos after successful reset
                  refreshPedidos();
                  // Close the modal
                  onClose();
                }}
              />
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      centered 
      scrollable 
      className={`mesa-controller-modal ${viewMode === 'payment' ? 'payment-mode' : ''}`}
      size="lg"
      dialogClassName="mesa-controller-dialog"
    >  
      <Modal.Header className="mesa-controller-header">
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            {isGroupMode && pedidoGroup ? (
              <>
                <FaUsers /> {pedidoGroup.name}
              </>
            ) : mesa ? (
              <>
                <MdOutlineTableRestaurant /> 
                {(mesa.tag === '' || mesa.tag === null) ? mesa.num : mesa.tag}
              </>
            ) : null}
          </Modal.Title>
        </div>
        
        {/* People counter controls - Only show for mesa mode, not group mode */}
        {!isGroupMode && mesa && (
          <div className="people-counter-container">
            <Button 
              variant="none" 
              className="people-counter-button people-minus"
              onClick={() => handleUpdatePeople(currentPeople - 1)}
              disabled={currentPeople <= 1 || updatingPeople}
            >
              <FaMinus size={12} />
            </Button>
            
            <div className="people-counter-display">
              <FaUser size={14} />
              <span className="people-count">{currentPeople}</span>
            </div>
            
            <Button 
              variant="none" 
              className="people-counter-button people-plus"
              onClick={() => handleUpdatePeople(currentPeople + 1)}
              disabled={updatingPeople}
            >
              <FaPlus size={12} />
            </Button>
          </div>
        )}

        {/* Smart Comment button - behavior changes based on view mode */}
        {(() => {
          const commentsCount = pedidos.filter(p => p.comment && p.comment.trim() !== '').length;
          const isOrderMode = viewMode === 'order';
          const showBadge = !isOrderMode && commentsCount > 0;
          
          return (
            <Button
              variant="none"
              className={`comment-icon-button ${pedidoComment && isOrderMode ? 'has-comment' : ''} ${showBadge ? 'has-badge' : ''}`}
              onClick={() => isOrderMode ? setShowCommentModal(true) : setShowCommentsViewer(true)}
              title={isOrderMode ? (pedidoComment || 'Agregar comentario') : `Ver ${commentsCount} comentario${commentsCount !== 1 ? 's' : ''}`}
            >
              <FaComment size={18} />
              {pedidoComment && isOrderMode && <span className="comment-indicator"></span>}
              {showBadge && <span className="comment-badge">{commentsCount}</span>}
            </Button>
          );
        })()}

        <Button variant="none" className="modal-close-button" onClick={onClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="mesa-controller-body">
        {/* Charged Banner - Shows when receipt has been generated (only in mesa/comandas view, not in payment view) */}
        {!isGroupMode && mesa && mesa.charged && viewMode !== 'payment' && (
          <div className="charged-info-banner">
            <FaReceipt className="banner-icon" />
            <span className="banner-text">Se ha generado una precuenta para esta mesa</span>
          </div>
        )}
        
        {loading && <MiniLoadingSpinner left="50%" />}
        {!loading && (
          <>
            {/* Show toggle for mesa mode with used state (not for virtual mesas) */}
            {!isGroupMode && mesa && mesa.used && !mesa.is_virtual && (viewMode === 'comandas' || viewMode === 'mesa') && renderToggle()}

            {viewMode === 'payment' && getPermissionState(129) && (mesa || pedidoGroup) &&
              <CobrarPedidos
                allowSplit={true} // Always allow split for restaurant mode (both mesas and groups)
                show={viewMode === 'payment'}
                autoCreateSingle={autoCreateSingle} // Pass autoCreateSingle flag from split button
                preselectedSplitMethod={selectedSplitMethod} // Pass the dropdown selection
                onHide={() => {
                  setViewMode('mesa');
                  setTargetSelections(undefined); // Clear target selections when closing
                  setAutoCreateSingle(false); // Reset auto-create flag
                  setSelectedSplitMethod(undefined); // Reset selected method
                  refreshPedidos(); // Refresh pedidos after payment
                }}
                mesa_id={mesa?.id ?? undefined}
                pedido_group_id={isGroupMode && pedidoGroup ? pedidoGroup.pedido_group_id : undefined}
                mesa={mesa ?? undefined}
                onClose={(partialPaymentSelections?: string[]) => {
                  // Only clear selections and close modal if this was a FULL payment
                  // For PARTIAL payments (partialPaymentSelections exists), keep modal open
                  const isPartialPayment = partialPaymentSelections && partialPaymentSelections.length > 0;

                  setTargetSelections(undefined); // Reset target selections
                  setAutoCreateSingle(false); // Reset auto-create flag
                  setSelectedSplitMethod(undefined); // Reset selected method
                  refreshPedidos(); // Refresh pedidos when payment completes
                  
                  // Only close the modal for full payments, not partial payments
                  if (!isPartialPayment) {
                    onClose();
                  }
                }}
                targetSelections={targetSelections} // Pass target selections for partial payments
                client_name={isGroupMode && pedidoGroup ? (pedidoGroup.user_name ?? undefined) : undefined}
                acceptChange={mesa?.is_virtual || isGroupMode} // Enable cash change for virtual tables and delivery/pickup groups
              />
            }

            {viewMode === 'comandas' && 
              <RenderComandas comandas={comandas} setComandas={setComandas} />
            }

            {viewMode === 'order' && (
                <OrderTaker
                  key={`order-mesa-${mesa?.id || pedidoGroup?.pedido_group_id || 'unknown'}-${mesa?.is_virtual ? 'virtual' : 'regular'}`}
                  pedido_type={mesa?.is_virtual ? 'Caja' : 'Restaurant'}
                  totalClients={currentPeople ?? peopleNumber ?? 1}
                  orderContext={orderContext}
                  setViewMode={setViewMode}
                  mesa={mesa ?? undefined}
                  onPedidoAdded={refreshPedidos}
                />
          )}

            {viewMode === 'mesa' && 
                <OrderViewer
                  mesa={mesa ?? undefined}
                  onClose={onClose}
                  pedidosList={pedidos}
                  allowEdit={getPermissionState(121)}
                  setBlocked={setBlocked}
                  onGenerateReceipt={(selections) => {
                    // When "Generar Recibo" is clicked with selected items
                    setTargetSelections(selections);
                    setViewMode('payment'); // Trigger the existing payment flow
                  }}
                  onSelectionModeChange={(isInSelectionMode) => {
                    setIsSelectionMode(isInSelectionMode);
                  }}
                  onMesaClosed={onMesaClosed}
                />
            }

          </>
        )}
      </Modal.Body>
      {viewMode === 'mesa' && renderFooter()}
      
      {/* Pedido Comment Modal */}
      <PedidoCommentModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        comment={pedidoComment}
        onCommentChange={setPedidoComment}
      />
      
      {/* Pedido Comments Viewer */}
      <PedidoCommentsViewer
        show={showCommentsViewer}
        onHide={() => setShowCommentsViewer(false)}
        pedidos={pedidos}
      />
    </Modal>
  );
};

export default MesaController;